import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import BookingStats from './EventBookingsComponents/BookingStats';
import BookingFilters from './EventBookingsComponents/BookingFilters';
import BookingTable from './EventBookingsComponents/BookingTable';
import ConfirmDialog from './EventBookingsComponents/ConfirmDialog';

const EventBookings = () => {
  const [bookings, setBookings] = useState({ active: [], inactive: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [businessId, setBusinessId] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token_partner_restaurant');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setBusinessId(payload.business_id);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (businessId) {
      fetchBookings();

      const interval = setInterval(() => {
        fetchBookings(true);
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [businessId]);

  const fetchBookings = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const token = localStorage.getItem('token_partner_restaurant');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/reservation/business/${businessId}/Restaurant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(response.data || { active: [], inactive: [] });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      const token = localStorage.getItem('token_partner_restaurant');
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/reservation/${reservationId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reservation ${newStatus.toLowerCase()} successfully`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update reservation status');
    }
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const filterBookings = (bookingsList) => {
    if (!bookingsList || !Array.isArray(bookingsList)) return [];
    return bookingsList.filter((booking) => {
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch =
        booking.booking_id?.email?.toLowerCase().includes(searchTerm) ||
        booking.booking_id?.name?.toLowerCase().includes(searchTerm) ||
        booking.booking_id?.phone_number?.includes(searchTerm) ||
        searchTerm === '' ||
        booking._id?.includes(searchTerm);

      const matchesStatus = !statusFilter || booking.status === statusFilter;

      if (selectedDate) {
        const bookingDate = parseDateString(booking.date);
        if (!bookingDate) return matchesSearch && matchesStatus;
        const filterDate = new Date(selectedDate);
        return (
          matchesSearch &&
          matchesStatus &&
          bookingDate.getFullYear() === filterDate.getFullYear() &&
          bookingDate.getMonth() === filterDate.getMonth() &&
          bookingDate.getDate() === filterDate.getDate()
        );
      }
      return matchesSearch && matchesStatus;
    });
  };

  const activeBookings = filterBookings(bookings.active);
  const inactiveBookings = filterBookings(bookings.inactive);

  const formatDisplayDate = (dateStr) => {
    const date = parseDateString(dateStr);
    if (!date) return dateStr || 'N/A';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Pending': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Confirmed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Completed': return 'bg-[#067978]/10 text-[#067978] border-[#067978]/20';
      case 'Cancelled': return 'bg-rose-50 text-rose-600 border-rose-100';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 space-y-3">
      <div className="w-10 h-10 border-4 border-[#067978] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-500 font-bold text-xs animate-pulse">Fetching your bookings...</p>
    </div>
  );

  return (
    <div className="space-y-4 p-4 sm:p-6 animate-in fade-in duration-700 pb-10">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-[#067978] tracking-tight">Bookings Management</h2>
          <p className="text-slate-500 text-xs font-medium mt-0.5">Manage and track all your restaurant reservations.</p>
        </div>
      </div>

      {/* Compact Stats Cards */}
      <BookingStats activeBookings={activeBookings} inactiveBookings={inactiveBookings} />

      {/* Compact Filters */}
      <BookingFilters
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
      />

      {/* Compact Table View Container */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100 px-4 bg-slate-50/50">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-3 text-xs font-extrabold transition-all relative ${activeTab === 'active' ? 'text-[#067978]' : 'text-slate-400 hover:text-slate-600'}`}
          >
            Active Reservations
            <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded-full text-[10px] text-slate-600 font-bold">{activeBookings.length}</span>
            {activeTab === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#067978] rounded-t-full"></div>}
          </button>

          <button
            onClick={() => setActiveTab('inactive')}
            className={`px-4 py-3 text-xs font-extrabold transition-all relative ${activeTab === 'inactive' ? 'text-[#067978]' : 'text-slate-400 hover:text-slate-600'}`}
          >
            Pending Approval
            <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded-full text-[10px] text-slate-600 font-bold">{inactiveBookings.length}</span>
            {activeTab === 'inactive' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#067978] rounded-t-full"></div>}
          </button>
        </div>

        <BookingTable
          bookings={activeTab === 'active' ? activeBookings : inactiveBookings}
          activeTab={activeTab}
          formatDisplayDate={formatDisplayDate}
          getStatusClass={getStatusClass}
          setSelectedBookingId={setSelectedBookingId}
          setShowConfirmDialog={setShowConfirmDialog}
          updateReservationStatus={updateReservationStatus}
        />
      </div>

      <ConfirmDialog
        show={showConfirmDialog}
        onConfirm={async () => {
          await updateReservationStatus(selectedBookingId, "Confirmed");
          setShowConfirmDialog(false);
          setSelectedBookingId(null);
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
};

export default EventBookings;
